/**
 * <ShareBar label="Flipkart" value="₹4.84 L" share={32.5} color={platformColor('FLIPKART')} onClick={...} />
 *
 * One horizontal share row: label + value (+ optional `extra` node, e.g. a DeltaBadge) above a thin bar.
 *   share     percent 0-100 of the whole (clamped); shown as "32.5%" unless showShare={false}.
 *             Compute it from revenue / total on the client if the API scale is unclear.
 *   sublabel  small grey line under the label (e.g. "ASP ₹2,340 · 12% off")
 *   onClick   makes the row a keyboard-reachable button; `active` highlights it (aria-pressed)
 *   ariaLabel extra action text (e.g. "Filter the page to Flipkart") appended to the row's visible
 *             content for screen readers; it does not replace the label/value/share.
 */
import React from 'react';

export const ShareBar = ({ label, value, share = 0, color = '#2563EB', sublabel, extra, onClick, active = false, showShare = true, ariaLabel, className = '', testId }) => {
  const pct = Math.max(0, Math.min(100, Number(share) || 0));
  const body = (
    <>
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="min-w-0 truncate font-medium text-gray-900">{label}</span>
        <span className="flex shrink-0 items-center gap-2 tabular-nums text-gray-900">
          {extra}
          <span>{value}</span>
          {showShare && <span className="w-12 text-right text-xs text-gray-500">{pct.toFixed(pct >= 10 ? 0 : 1)}%</span>}
        </span>
      </div>
      {sublabel && <div className="mt-0.5 truncate text-xs text-gray-500">{sublabel}</div>}
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full transition-[width] duration-300" style={{ width: `${pct}%`, background: color }} />
      </div>
    </>
  );
  const shell = `block w-full rounded-md px-2 py-1.5 text-left ${className}`;
  if (!onClick) return <div className={shell} data-testid={testId}>{body}</div>;
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={`${shell} transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${active ? 'bg-blue-50 ring-1 ring-blue-200' : ''}`}
      data-testid={testId}>
      {body}
      {/* ariaLabel is appended (not a replacement) so the row's label, value and share stay in the accessible name. */}
      {ariaLabel && <span className="sr-only">{`. ${ariaLabel}`}</span>}
    </button>
  );
};

export default ShareBar;
